<script setup>
import { ref, watch, onMounted } from 'vue';
import sicSupportAPI from 'dashboard/api/sicSupport';

// ── Aba activa ──────────────────────────────────────────────────────────
const activeTab = ref('sync');

// ── Aba Sincronização ───────────────────────────────────────────────────
const status = ref(null);
const isSyncing = ref(false);
const isChecking = ref(false);
const syncResult = ref(null);
const syncError = ref(null);

async function checkStatus() {
  isChecking.value = true;
  syncError.value = null;
  try {
    const res = await sicSupportAPI.check();
    status.value = res.data;
  } catch {
    syncError.value = 'Erro ao verificar status do Qdrant.';
  } finally {
    isChecking.value = false;
  }
}

async function syncKnowledge() {
  isSyncing.value = true;
  syncResult.value = null;
  syncError.value = null;
  try {
    const res = await sicSupportAPI.syncKnowledge();
    syncResult.value = res.data;
    await checkStatus();
  } catch (e) {
    syncError.value =
      e?.response?.data?.error || 'Erro ao sincronizar base de conhecimento.';
  } finally {
    isSyncing.value = false;
  }
}

// ── Aba Editor ──────────────────────────────────────────────────────────
const files = ref([]);
const selectedFile = ref('');
const fileContent = ref('');
const chunks = ref([]);
const isLoadingFile = ref(false);
const isSaving = ref(false);
const saveMsg = ref('');
const saveError = ref('');
let debounceTimer = null;

const CHUNK_LIMIT = 500;
const CHUNK_OVERLAP = 50;

function buildChunks(content, fileKey) {
  if (!content || !fileKey) return [];
  const sections = content.split(/^(?=#{2,3} )/m);
  const result = [];
  let idx = 0;
  for (const raw of sections) {
    const section = raw.trim();
    if (!section) continue;
    const words = section.split(/\s+/);
    if (words.length <= CHUNK_LIMIT) {
      result.push({
        id: `sic_${fileKey}_${idx++}`,
        text: section,
        words: words.length,
      });
    } else {
      let i = 0;
      while (i < words.length) {
        const slice = words.slice(i, i + CHUNK_LIMIT);
        result.push({
          id: `sic_${fileKey}_${idx++}`,
          text: slice.join(' '),
          words: slice.length,
        });
        i += CHUNK_LIMIT - CHUNK_OVERLAP;
      }
    }
  }
  return result;
}

function scheduleChunkPreview() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    chunks.value = buildChunks(fileContent.value, selectedFile.value);
  }, 400);
}

watch(fileContent, scheduleChunkPreview);

async function loadFiles() {
  if (files.value.length) return;
  try {
    const res = await sicSupportAPI.listFiles();
    files.value = res.data.files || [];
  } catch {
    // silently ignore
  }
}

async function onSelectFile() {
  if (!selectedFile.value) return;
  isLoadingFile.value = true;
  saveMsg.value = '';
  saveError.value = '';
  try {
    const res = await sicSupportAPI.getFile(selectedFile.value);
    fileContent.value = res.data.content || '';
    chunks.value = buildChunks(fileContent.value, selectedFile.value);
  } catch {
    saveError.value = 'Erro ao carregar arquivo.';
  } finally {
    isLoadingFile.value = false;
  }
}

async function saveFile() {
  if (!selectedFile.value) return;
  isSaving.value = true;
  saveMsg.value = '';
  saveError.value = '';
  try {
    await sicSupportAPI.saveFile(selectedFile.value, fileContent.value);
    saveMsg.value = 'Arquivo salvo!';
    setTimeout(() => {
      saveMsg.value = '';
    }, 3000);
  } catch (e) {
    saveError.value = e?.response?.data?.error || 'Erro ao salvar arquivo.';
  } finally {
    isSaving.value = false;
  }
}

function onTabEditor() {
  activeTab.value = 'editor';
  loadFiles();
}

// Resumo do chunk para exibir no painel (primeiras 3 linhas não-vazias)
function chunkPreview(text) {
  return text
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .slice(0, 3)
    .join('\n');
}

// ── Aba Escalada / Suporte Humano ───────────────────────────────────────
const cfg = ref({
  escalation_enabled: true,
  notification_phones: [],
  cooldown_minutes: 360,
  screenshot_enabled: true,
  strong_tier_enabled: false,
});
const cfgAvailable = ref(true);
const cfgSaving = ref(false);
const cfgMsg = ref('');
const cfgError = ref('');
const testMsg = ref('');
const newPhone = ref('');

async function loadConfig() {
  cfgError.value = '';
  try {
    const res = await sicSupportAPI.getConfig();
    cfgAvailable.value = res.data.available;
    cfg.value = {
      escalation_enabled: res.data.escalation_enabled,
      notification_phones: res.data.notification_phones || [],
      cooldown_minutes: res.data.cooldown_minutes || 360,
      screenshot_enabled: res.data.screenshot_enabled,
      strong_tier_enabled: res.data.strong_tier_enabled,
    };
  } catch {
    cfgError.value = 'Erro ao carregar configuração.';
  }
}

function addPhone() {
  const p = (newPhone.value || '').replace(/\D/g, '');
  if (p && !cfg.value.notification_phones.includes(p)) {
    cfg.value.notification_phones.push(p);
  }
  newPhone.value = '';
}

function removePhone(p) {
  cfg.value.notification_phones = cfg.value.notification_phones.filter(
    x => x !== p
  );
}

async function saveConfig() {
  cfgSaving.value = true;
  cfgMsg.value = '';
  cfgError.value = '';
  try {
    await sicSupportAPI.updateConfig(cfg.value);
    cfgMsg.value = 'Configuração salva!';
    setTimeout(() => {
      cfgMsg.value = '';
    }, 3000);
  } catch (e) {
    cfgError.value = e?.response?.data?.error || 'Erro ao salvar configuração.';
  } finally {
    cfgSaving.value = false;
  }
}

async function sendTest() {
  testMsg.value = 'Enviando...';
  try {
    const res = await sicSupportAPI.testEscalation();
    testMsg.value = res.data.sent
      ? `Enviado para ${res.data.delivered} número(s).`
      : `Não enviado (${res.data.reason || 'sem telefones'}).`;
  } catch (e) {
    testMsg.value = e?.response?.data?.error || 'Erro no teste.';
  }
}

function onTabEscalation() {
  activeTab.value = 'escalada';
  loadConfig();
}

onMounted(() => {
  checkStatus();
});
</script>

<template>
  <div class="mx-auto max-w-6xl py-6 px-4">
    <!-- Cabeçalho -->
    <div class="mb-5">
      <h2 class="text-xl font-semibold text-n-slate-12">
        Suporte IA — Base de Conhecimento
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        Gerencie a base de conhecimento do assistente de suporte interno do SIC.
      </p>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 mb-5 border-b border-n-weak">
      <button
        class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'sync'
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'sync'"
      >
        Sincronização
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'editor'
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabEditor"
      >
        Editor de Conhecimento
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'escalada'
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabEscalation"
      >
        Escalada / Suporte Humano
      </button>
    </div>

    <!-- ── ABA: SINCRONIZAÇÃO ─────────────────────────────────────────── -->
    <div v-if="activeTab === 'sync'" class="max-w-2xl">
      <!-- Status card -->
      <div class="rounded-xl border border-n-weak bg-n-background p-5 mb-5">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium text-n-slate-12">Status da base de conhecimento</span>
          <button
            class="text-xs text-woot-500 hover:text-woot-600 disabled:opacity-50"
            :disabled="isChecking"
            @click="checkStatus"
          >
            {{ isChecking ? 'Verificando...' : 'Atualizar' }}
          </button>
        </div>

        <div v-if="status" class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="inline-block w-2 h-2 rounded-full"
              :class="status.configured ? 'bg-emerald-500' : 'bg-amber-400'"
            />
            <span class="text-sm text-n-slate-11">
              {{
                status.configured
                  ? 'Configurado e ativo'
                  : 'Aguardando primeira sincronização'
              }}
            </span>
          </div>
          <p v-if="status.chunks_count" class="text-sm text-n-slate-11">
            Chunks indexados:
            <strong class="text-n-slate-12">{{ status.chunks_count }}</strong>
          </p>
          <p v-if="status.last_synced_at" class="text-xs text-n-slate-10">
            Última sincronização:
            {{ new Date(status.last_synced_at).toLocaleString('pt-BR') }}
          </p>
        </div>
        <p v-else-if="isChecking" class="text-sm text-n-slate-10">
          Verificando...
        </p>
        <p v-else class="text-sm text-n-slate-10">Status indisponível.</p>
      </div>

      <!-- Sync result -->
      <div
        v-if="syncResult"
        class="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900 p-4 mb-5"
      >
        <p
          class="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-1"
        >
          Sincronização concluída!
        </p>
        <p class="text-xs text-emerald-600 dark:text-emerald-500">
          {{ syncResult.synced_chunks }} chunks indexados em
          {{ syncResult.files?.length || 0 }} arquivos.
        </p>
        <div v-if="syncResult.files?.length" class="mt-2 flex flex-wrap gap-1">
          <span
            v-for="f in syncResult.files"
            :key="f"
            class="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded"
          >
            {{ f }}
          </span>
        </div>
      </div>

      <!-- Error -->
      <div
        v-if="syncError"
        class="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-4 mb-5"
      >
        <p class="text-sm text-red-700 dark:text-red-400">{{ syncError }}</p>
      </div>

      <!-- Sync button -->
      <button
        class="w-full rounded-lg bg-woot-500 hover:bg-woot-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 text-sm transition-colors"
        :disabled="isSyncing"
        @click="syncKnowledge"
      >
        <span v-if="isSyncing" class="flex items-center justify-center gap-2">
          <span class="i-lucide-loader-2 animate-spin w-4 h-4" />
          Sincronizando...
        </span>
        <span v-else>Sincronizar Base de Conhecimento</span>
      </button>

      <p class="mt-3 text-xs text-n-slate-10 text-center">
        A sincronização lê todos os arquivos
        <code class="font-mono">doc/sic/*.md</code> e atualiza o índice
        vetorial. Leva cerca de 30–60 segundos.
      </p>
    </div>

    <!-- ── ABA: EDITOR ───────────────────────────────────────────────── -->
    <div v-if="activeTab === 'editor'">
      <!-- Seletor de arquivo -->
      <div class="flex items-center gap-3 mb-4">
        <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap">Arquivo:</label>
        <select
          v-model="selectedFile"
          class="flex-1 max-w-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-woot-500"
          @change="onSelectFile"
        >
          <option value="">— selecione —</option>
          <option v-for="f in files" :key="f.key" :value="f.key">
            {{ f.key }} · {{ f.name }}
          </option>
        </select>
        <span
          v-if="isLoadingFile"
          class="i-lucide-loader-2 animate-spin w-4 h-4 text-n-slate-10"
        />
      </div>

      <!-- Split pane -->
      <div
        v-if="selectedFile"
        class="grid grid-cols-2 gap-4"
        style="height: calc(100vh - 280px); min-height: 500px"
      >
        <!-- Painel esquerdo: editor -->
        <div
          class="flex flex-col rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-3 py-2 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="text-xs font-medium text-n-slate-11">Editar Markdown</span>
            <span class="text-xs text-n-slate-10 font-mono">{{ selectedFile }}.md</span>
          </div>
          <textarea
            v-model="fileContent"
            class="flex-1 w-full resize-none bg-n-background text-n-slate-12 text-sm font-mono p-3 focus:outline-none leading-relaxed"
            placeholder="Selecione um arquivo para editar..."
            spellcheck="false"
          />
        </div>

        <!-- Painel direito: chunks -->
        <div
          class="flex flex-col rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-3 py-2 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="text-xs font-medium text-n-slate-11">Preview de Chunks</span>
            <span class="text-xs text-n-slate-10">{{ chunks.length }} chunks</span>
          </div>
          <div class="flex-1 overflow-y-auto p-3 space-y-2">
            <div
              v-for="(chunk, i) in chunks"
              :key="chunk.id"
              class="rounded-lg border border-n-weak bg-n-background p-3"
            >
              <div class="flex items-center justify-between mb-1.5">
                <span
                  class="text-xs font-mono font-medium text-woot-600 dark:text-woot-400"
                >
                  CHUNK {{ i }}
                </span>
                <span
                  class="text-xs px-1.5 py-0.5 rounded font-medium"
                  :class="
                    chunk.words > 400
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                >
                  {{ chunk.words }} palavras
                </span>
              </div>
              <pre
                class="text-xs text-n-slate-11 whitespace-pre-wrap leading-relaxed font-mono"
                >{{ chunkPreview(chunk.text) }}</pre>
              <p
                v-if="chunk.text.split('\n').filter(Boolean).length > 3"
                class="text-xs text-n-slate-10 mt-1 italic"
              >
                …
              </p>
            </div>
            <p
              v-if="!chunks.length"
              class="text-sm text-n-slate-10 text-center py-8"
            >
              Nenhum chunk gerado. Verifique se o arquivo tem conteúdo.
            </p>
          </div>
        </div>
      </div>

      <!-- Placeholder quando nenhum arquivo selecionado -->
      <div
        v-else
        class="rounded-xl border border-dashed border-n-weak flex items-center justify-center text-n-slate-10 text-sm"
        style="height: 300px"
      >
        Selecione um arquivo acima para editar
      </div>

      <!-- Rodapé com botões -->
      <div v-if="selectedFile" class="flex items-center justify-between mt-4">
        <div class="flex items-center gap-2">
          <!-- Feedback save -->
          <span
            v-if="saveMsg"
            class="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1"
          >
            <span class="i-lucide-check w-3.5 h-3.5" />
            {{ saveMsg }}
          </span>
          <span v-if="saveError" class="text-xs text-red-500">{{
            saveError
          }}</span>
        </div>
        <div class="flex gap-2">
          <button
            class="rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-2 text-n-slate-12 text-sm font-medium py-2 px-4 transition-colors disabled:opacity-50"
            :disabled="isSaving"
            @click="saveFile"
          >
            <span v-if="isSaving" class="flex items-center gap-1.5">
              <span class="i-lucide-loader-2 animate-spin w-3.5 h-3.5" />
              Salvando...
            </span>
            <span v-else class="flex items-center gap-1.5">
              <span class="i-lucide-save w-3.5 h-3.5" />
              Salvar arquivo
            </span>
          </button>
          <button
            class="rounded-lg bg-woot-500 hover:bg-woot-600 disabled:opacity-60 text-white text-sm font-medium py-2 px-4 transition-colors"
            :disabled="isSyncing"
            @click="syncKnowledge"
          >
            <span v-if="isSyncing" class="flex items-center gap-1.5">
              <span class="i-lucide-loader-2 animate-spin w-3.5 h-3.5" />
              Sincronizando...
            </span>
            <span v-else class="flex items-center gap-1.5">
              <span class="i-lucide-upload-cloud w-3.5 h-3.5" />
              Sincronizar Qdrant
            </span>
          </button>
        </div>
      </div>

      <!-- Sync result no editor tab -->
      <div
        v-if="syncResult && activeTab === 'editor'"
        class="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900 p-3"
      >
        <p class="text-xs text-emerald-700 dark:text-emerald-400">
          Sincronizado! {{ syncResult.synced_chunks }} chunks em
          {{ syncResult.files?.length || 0 }} arquivos.
        </p>
      </div>
    </div>

    <!-- ── ABA: ESCALADA / SUPORTE HUMANO ──────────────────────────────── -->
    <div v-if="activeTab === 'escalada'" class="max-w-2xl">
      <div
        v-if="!cfgAvailable"
        class="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-4 mb-5"
      >
        <p class="text-sm text-amber-700 dark:text-amber-400">
          A configuração ainda não está disponível: a migration
          <code class="font-mono">sic_support_configs</code> precisa ser
          aplicada (<code class="font-mono">rails db:migrate</code>).
        </p>
      </div>

      <div
        class="rounded-xl border border-n-weak bg-n-background p-5 mb-5 space-y-4"
      >
        <label class="flex items-start gap-2 text-sm text-n-slate-12">
          <input
            v-model="cfg.escalation_enabled"
            type="checkbox"
            class="mt-0.5"
          />
          <span>Acionar o suporte humano por WhatsApp quando o IntelliChat não
            resolver (ou quando o cliente pedir um atendente).</span>
        </label>

        <div>
          <p class="text-sm font-medium text-n-slate-12 mb-2">
            Telefones de notificação (WhatsApp do suporte humano)
          </p>
          <div class="flex flex-wrap gap-2 mb-2">
            <span
              v-for="p in cfg.notification_phones"
              :key="p"
              class="inline-flex items-center gap-1.5 text-xs bg-n-alpha-2 text-n-slate-12 px-2 py-1 rounded"
            >
              {{ p }}
              <button
                class="text-red-500 hover:text-red-600"
                @click="removePhone(p)"
              >
                ×
              </button>
            </span>
            <span
              v-if="!cfg.notification_phones.length"
              class="text-xs text-n-slate-10"
            >
              Nenhum telefone cadastrado — usará o global, se houver.
            </span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="newPhone"
              placeholder="5562999999999 (com DDI + DDD)"
              class="flex-1 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @keyup.enter="addPhone"
            />
            <button
              class="rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-2 text-n-slate-12 text-sm px-3 py-1.5"
              @click="addPhone"
            >
              Adicionar
            </button>
          </div>
        </div>

        <label class="flex items-start gap-2 text-sm text-n-slate-12">
          <input
            v-model="cfg.screenshot_enabled"
            type="checkbox"
            class="mt-0.5"
          />
          <span>Permitir que o IntelliChat tire screenshots da conta para
            diagnosticar/ilustrar.</span>
        </label>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <button
            class="rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-2 text-n-slate-12 text-sm font-medium py-2 px-4"
            @click="sendTest"
          >
            Enviar teste
          </button>
          <span v-if="testMsg" class="text-xs text-n-slate-11">{{
            testMsg
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="cfgMsg"
            class="text-xs text-emerald-600 dark:text-emerald-400"
            >{{ cfgMsg }}</span>
          <span v-if="cfgError" class="text-xs text-red-500">{{
            cfgError
          }}</span>
          <button
            class="rounded-lg bg-woot-500 hover:bg-woot-600 disabled:opacity-60 text-white text-sm font-medium py-2 px-4 transition-colors"
            :disabled="cfgSaving"
            @click="saveConfig"
          >
            {{ cfgSaving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
